import { supabase } from "../lib/supabaseClient";
import { ConnectedApp } from "../types";
import { isAuthMockMode, wait } from "./auth";
import { MCP_BASE_URL } from "../config";

export const AVAILABLE_APPS = [
  { id: "gmail", name: "Gmail", icon: "gmail" },
  { id: "calendar", name: "Google Calendar", icon: "calendar" },
  { id: "drive", name: "Google Drive", icon: "drive" },
  { id: "docs", name: "Google Docs", icon: "docs" },
  { id: "sheets", name: "Google Sheets", icon: "sheets" },
  { id: "meet", name: "Google Meet", icon: "meet" },
  { id: "notion", name: "Notion", icon: "notion" },
];

let mockApps: ConnectedApp[] = AVAILABLE_APPS.map((app) => ({
  ...app,
  status: "connect",
}));

export const getIntegrations = async (): Promise<ConnectedApp[]> => {
  if (isAuthMockMode) {
    await wait(500);
    return [...mockApps];
  }

  try {
    const { data: { session } } = await supabase!.auth.getSession();
    const user = session?.user;
    
    if (!user) {
      console.warn("[Integrations] No user found in session.");
      return AVAILABLE_APPS.map((a) => ({ ...a, status: "connect" }));
    }

    console.log(`[Integrations] Fetching for user: ${user.id}`);
    let dbIntegrations: any[] = [];

    // Strategy 1: Attempt to fetch via Backend Proxy (bypasses RLS using service key)
    try {
      const resp = await fetch(`${MCP_BASE_URL}/api/integrations`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      if (resp.ok) {
        dbIntegrations = await resp.json();
        console.log("[Integrations] Fetched via Backend:", dbIntegrations);
      } else {
        throw new Error(`Backend fetch failed with ${resp.status}`);
      }
    } catch (proxyErr) {
      console.warn("[Integrations] Backend proxy fetch failed, trying direct Supabase query...", proxyErr);
      // Strategy 2: Fallback to direct Supabase query (subject to RLS)
      const { data, error } = await supabase!
        .from("user_integrations")
        .select("app_id, status")
        .eq("user_id", user.id);

      if (error) {
        console.error("[Integrations] Direct Supabase query error:", error);
        return AVAILABLE_APPS.map((a) => ({ ...a, status: "connect" }));
      }
      dbIntegrations = data || [];
      console.log("[Integrations] Fetched via Direct query:", dbIntegrations);
    }

    return AVAILABLE_APPS.map((app) => {
      const found = dbIntegrations?.find((i) => {
        const dbAppId = (i.app_id || "").toLowerCase().trim();
        const targetAppId = app.id.toLowerCase().trim();
        return dbAppId === targetAppId;
      });
      
      const isConnected = found && (
        found.status?.toLowerCase().trim() === "connected" || 
        found.status?.toLowerCase().trim() === "active" ||
        found.status?.toLowerCase().trim() === "enabled"
      );

      return {
        ...app,
        status: isConnected ? "connected" : "connect",
      } as ConnectedApp;
    });
  } catch (err) {
    console.error("[Integrations] Critical fetch error:", err);
    return AVAILABLE_APPS.map((a) => ({ ...a, status: "connect" }));
  }
};

export const toggleIntegration = async (appId: string, forcedAction?: 'connect' | 'disconnect'): Promise<ConnectedApp[]> => {
  if (isAuthMockMode) {
    mockApps = mockApps.map((app) =>
      app.id === appId
        ? {
            ...app,
            status: app.status === "connected" ? "connect" : "connected",
          }
        : app,
    );
    return [...mockApps];
  }

  const {
    data: { user },
  } = await supabase!.auth.getUser();
  if (!user) throw new Error("Not logged in");

  const { data: existing } = await supabase!
    .from("user_integrations")
    .select("*")
    .eq("user_id", user.id)
    .eq("app_id", appId)
    .maybeSingle();

  const newStatus = forcedAction
    ? (forcedAction === 'connect' ? 'connected' : 'disconnected')
    : (existing ? 'disconnected' : 'connected');

  if (newStatus === "connected") {
    console.log(`[Auth] Connecting ${appId} for user ${user.id}...`);
    try {
      const authUrl = `${MCP_BASE_URL}/auth/login?app_id=${appId}&user_id=${user.id}`;
      const width = 500;
      const height = 600;
      const left = (window.innerWidth - width) / 2 + window.screenX;
      const top = (window.innerHeight - height) / 2 + window.screenY;

      const popup = window.open(authUrl, "Google Auth", `width=${width},height=${height},left=${left},top=${top}`);
      if (!popup) throw new Error("Popup blocked.");

      await new Promise<void>((resolve) => {
        const messageHandler = (event: MessageEvent) => {
          if (event.data === "oauth_success") {
            window.removeEventListener("message", messageHandler);
            resolve();
          }
        };
        window.addEventListener("message", messageHandler);
        const timer = setInterval(() => {
          try { if (popup.closed) { window.removeEventListener("message", messageHandler); clearInterval(timer); resolve(); } } catch (e) {}
        }, 500);
      });
    } catch (authError) {
      console.error(`[Auth] Failed to authenticate ${appId}:`, authError);
      return getIntegrations();
    }
    await new Promise((r) => setTimeout(r, 1000));
  } else {
    console.log(`[Auth] Disconnecting ${appId} for user ${user.id}...`);

    const { error: deleteError, count } = await supabase!
      .from("user_integrations")
      .delete({ count: 'exact' })
      .match({ user_id: user.id, app_id: appId });

    console.log(`[Auth] Delete status: ${count} rows affected. Error:`, deleteError);

    if (deleteError) {
      console.warn(`[Auth] Precise delete failed, trying fallback for ${appId}`);
      await supabase!
        .from("user_integrations")
        .delete()
        .ilike("app_id", appId)
        .eq("user_id", user.id);
    }
  }

  return getIntegrations();
};
